import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import PublicIcon from "@mui/icons-material/Public";
import { DbJobInfos } from "../../../Core/Api/Types/db-job-infos";
import { JobStatus } from "../../../Core/Api/Types/job-status";
import DataObjectIcon from "@mui/icons-material/DataObject";
import NotesIcon from "@mui/icons-material/Notes";
import DataTable, { CellSize } from "../../Table/table";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { JobLog } from "../../../Core/Api/Types/job-log";
import { JobLogLevel } from "../../../Core/Api/Types/job-log-level";
import moment from "moment";
import FormSection from "../../Forms/FormSection/form-section";
import CodeBox from "../../Misc/CodeBox/code-box";
import { jobBrokerService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { AclMatch, UserHasAcls } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import TableCellTypography from "../../Table/TableCellTypography/table-cell-typography";
import { useTranslation } from "react-i18next";

export type ModalJobDetailsElementProps = {
  onClose?: (props: any) => void;
  job: DbJobInfos;
  onRetryCallback?: (job: DbJobInfos) => void;
  onResyncCallback?: (job: DbJobInfos) => void;
  onAbortCallback?: (job: DbJobInfos) => void;
  onBypassJobResultCallback?: (job: DbJobInfos) => void;
};

export type ModalJobDetailsProps = CommonModalProps<ModalJobDetailsElementProps>;

const ModalJobDetails = React.forwardRef<any, ModalJobDetailsProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, ...elementProps } = props;
  const { job } = elementProps;
  const customSnackbar = useCustomSnackbar();

  const abort = () => {
    jobBrokerService.Abort(job.id).then((response) => {
      if (response.success) customSnackbar(t("alert:success.jobAborted"), "success");
      else customSnackbar(t("alert:error.jobCouldNotBeAborted", { message: response.message }), "error");
      closeModal();
    });
  };

  const retry = () => {
    jobBrokerService.Retry(job.id, false).then((response) => {
      if (response.success) customSnackbar(t("alert:success.jobRetried"), "success");
      else customSnackbar(t("alert:error.jobCouldNotBeRetried", { message: response.message }), "error");
      closeModal();
    });
  };

  const resync = () => {
    jobBrokerService.NotifyResync(job.id).then((response) => {
      if (response.success) customSnackbar(t("alert:success.jobResync"), "success");
      else customSnackbar(t("alert:error.jobCouldNotBeResynced", { message: response.message }), "error");
      closeModal();
    });
  };

  const bypassJobResult = () => {
    jobBrokerService.BypassJobResult(job.id).then((response) => {
      if (response.success) customSnackbar(t("alert:success.jobBypass"), "success");
      else customSnackbar(t("alert:error.jobCouldNotBeBypassed", { message: response.message }), "error");
      closeModal();
    });
  };

  const rows: GridRowsProp = job?.logs as JobLog[];
  type Row = (typeof rows)[number];
  const columns: GridColDef[] = [
    {
      field: "level",
      headerName: t("common:level"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: Row) => {
        return (
          <>
            {data.value === JobLogLevel.Info && <Chip label={t("common:info")} color="info" />}
            {data.value === JobLogLevel.Error && <Chip label={t("common:error")} color="error" />}
          </>
        );
      },
    },
    {
      field: "time",
      headerName: t("common:time"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: Row) => (
        <Stack>
          <Typography variant="body1" fontWeight={100}>
            {moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_HOURS)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "message",
      headerName: t("common:message"),
      minWidth: CellSize.xxlarge,
      flex: CellSize.xxlarge,
      renderCell: (data: Row) => <TableCellTypography data={data.value} variant="body2" />,
    },
  ];

  return (
    <Modal
      title={t("modal:title.jobDetails", { id: job.id })}
      closeModal={closeModal}
      width={800}
      reference={ref}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            color="success"
            disabled={
              job.status !== JobStatus.ABORTED && job.status !== JobStatus.FAILED && job.status !== JobStatus.COMPLETED
            }
            onClick={() => {
              if (props.onRetryCallback) props.onRetryCallback(job);
              else retry();
            }}
          >
            {t("common:retry")}
          </Button>

          <Button
            color="error"
            disabled={job.status !== JobStatus.RUNNING}
            onClick={() => {
              if (props.onAbortCallback) props.onAbortCallback(job);
              else abort();
            }}
          >
            {t("common:abort")}
          </Button>

          <Button
            onClick={() => {
              if (props.onResyncCallback) props.onResyncCallback(job);
              else resync();
            }}
          >
            {t("common:resync")}
          </Button>

          {job.isPostActionBypassable && (
            <Button
              onClick={() => {
                if (props.onBypassJobResultCallback) props.onBypassJobResultCallback(job);
                else bypassJobResult();
              }}
            >
              {t("common:bypass")}
            </Button>
          )}

          <Button variant="outlined" onClick={closeModal}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <Box>
        <FormSection
          defaultExpanded={true}
          title={t("form:section.title.globalJobInfos")}
          icon={<PublicIcon />}
          sx={{ mb: 2 }}
        >
          <Stack spacing={1}>
            <MetadataListTextItem title={t("common:summarize")}>
              <Typography sx={{ overflowWrap: "break-word" }}>{job.summarize}</Typography>
            </MetadataListTextItem>
            <MetadataListTextItem title={t("common:status")}>{job.status}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:step")}>{job.step}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:priority")}>{job.priority}</MetadataListTextItem>
            <MetadataListTextItem title={t("menu:item.objectId")}>{job.objectId}</MetadataListTextItem>
            <MetadataListTextItem title={t("menu:item.fileId")}>
              {job.fileId ? "#" + job.fileId : "-"}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:item.destinationFileId")}>
              {job.destinationFileId ? "#" + job.destinationFileId : "-"}
            </MetadataListTextItem>
            {/* todo link */}
            <MetadataListTextItem title={t("menu:item.workflowInstanceId")}>
              {job.workflowInstanceId || "-"}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:item.triggeredByJobRole")}>
              {job.ruleId != null ? "#" + job.ruleId + " (" + job.ruleName + ")" : "-"}
            </MetadataListTextItem>
            {job.parentJobId !== null && (
              <MetadataListTextItem title={t("menu:item.parentJobId")}>
                {job.parentJobId != null ? "#" + job.parentJobId : "-"}
              </MetadataListTextItem>
            )}
            {job.owner !== null && (
              <MetadataListTextItem title={t("menu:item.triggeredByUser")}>
                {job.owner.firstName != null ? job.owner.firstName + " " + job.owner.lastName : "-"}
              </MetadataListTextItem>
            )}
          </Stack>
        </FormSection>

        <FormSection defaultExpanded={true} title={t("common:logs")} icon={<NotesIcon />} sx={{ mb: 2 }}>
          {job.logs !== null && job.logs.length === 0 && (
            <Typography component="span" fontWeight={500} color="text.tier">
              {t("menu:item.noLogsRegistered")}
            </Typography>
          )}
          {job.logs !== null && job.logs.length > 0 && (
            <Box mt={2}>
              <DataTable
                noControls
                rows={job.logs}
                columns={columns}
                columnHeaderHeight={48}
                rowHeight={56}
                getRowId={(row: any) => row.$id}
                getRowHeight={() => "auto"}
                disableRowSelectionOnClick
              />
            </Box>
          )}
        </FormSection>

        <FormSection defaultExpanded={true} title={t("form:section.title.jobInJson")} icon={<DataObjectIcon />}>
          <Stack spacing={1}>
            <CodeBox text={job.job} />
          </Stack>
        </FormSection>

        {(job.job as any).xml && UserHasAcls([AclTypes.aclAdmin, AclTypes.aclSuperUser], AclMatch.OneAtLeast) && (
          <FormSection
            defaultExpanded={true}
            title={t("form:section.title.xmlViewer")}
            icon={<DataObjectIcon />}
            sx={{ mt: 2 }}
          >
            <Stack spacing={1}>
              <CodeBox text={(job.job as any).xml} xml={true} />
            </Stack>
          </FormSection>
        )}
        {(job.job as any).json && UserHasAcls([AclTypes.aclAdmin, AclTypes.aclSuperUser], AclMatch.OneAtLeast) && (
          <FormSection
            defaultExpanded={true}
            title={t("form:section.title.jsonViewer")}
            icon={<DataObjectIcon />}
            sx={{ mt: 2 }}
          >
            <Stack spacing={1}>
              <CodeBox text={(job.job as any).json} xml={true} />
            </Stack>
          </FormSection>
        )}
      </Box>
    </Modal>
  );
});

export default ModalJobDetails;
